import {
  inject,
  signal,
  OnInit,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  faCog,
  faFile,
  IconDefinition,
  faFileCirclePlus,
} from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  DialogCloseReason,
  DialogCloseOptions,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';

import { AuditTrailComponent } from 'src/app/shared/components/audit-trail/audit-trail.component';
import { ExportTemplatesEditorComponent } from './export-templates-editor/export-templates-editor.component';

import { TabConfig, SearchableDialog } from 'src/app/shared/utils';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import { ExportTemplate } from 'src/app/core/models/core.models';
import { injectSaveExportTemplateMutation } from 'src/app/api/queries/export-template.queries';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { DialogTabNavComponent } from '../../../shared/components/dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

interface DialogData extends TabDialogDataBase {
  exportTemplate: ExportTemplate | null;
  companyId: string | null;
}

export interface ExportTemplateDialogResult {
  /** The submitted form values (what the editor had on screen). */
  exportTemplate: ExportTemplate;
  /**
   * The in-flight save. The dialog closes optimistically, so hosts that need the
   * persisted template (e.g. the grid, to refresh its row) await this.
   */
  saved: Promise<ExportTemplate>;
}

@Component({
  selector: 'app-export-templates-editor-dialog',
  templateUrl: './export-templates-editor-dialog.component.html',
  styleUrl: './export-templates-editor-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    TranslatePipe,
  ],
})
export class ExportTemplatesEditorDialogComponent
  extends TabDialogBase<DialogData, ExportTemplateDialogResult>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);

  private readonly saveExportTemplateMutation =
    injectSaveExportTemplateMutation();

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  objectActionIcon: IconDefinition | IconProp;
  objectIcon: IconDefinition | IconProp;

  objectActionTitle = '';
  // A signal: the tab manager's form stream emits from a signal effect, after
  // the template in the same pass (WAP-1954).
  readonly objectTitle = signal('');
  objectTitleDefault = this.translateService.instant(
    'popup.object-title.untitled',
  );

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  get isUpdateMode(): boolean {
    return !!this.dialogData.exportTemplate;
  }

  ngOnInit(): void {
    this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);

    // ! update objectTitle dynamically
    this.tabManager.activeFormValueChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formData) => {
        if (this.isUpdateMode) {
          this.objectTitle.set(formData?.name ?? this.objectTitle());
        } else {
          this.objectTitle.set(formData?.name || this.objectTitleDefault);
        }
      });
  }

  private setupUIState(): void {
    if (this.isUpdateMode) {
      this.objectActionIcon = faCog;
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.edit-export-template-settings',
      );

      this.objectTitle.set(this.dialogData.exportTemplate.name);
      this.objectIcon = getIconForDataObject(
        this.dialogData.exportTemplate.object_class_id,
      );
    } else {
      // ! Create Mode
      this.objectActionIcon = faFileCirclePlus;
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.create-new-export-template',
      );

      this.objectIcon = faFile;
      // ! objectTitle is updated dynamically
    }
  }

  private setupTabs(): void {
    this.tabs = [
      {
        id: 'general',
        label: this.translateService.instant(
          'export-template-editor.tab.general',
        ),
        component: ExportTemplatesEditorComponent,
        inputs: {
          searchString$: this.searchString$,
          exportTemplate: this.dialogData.exportTemplate,
          companyId: this.dialogData.companyId,
        },
      },
    ];

    if (this.isUpdateMode) {
      this.tabs.push({
        id: 'audit-trail',
        label: this.translateService.instant(
          'export-template-editor.tab.audit-trail',
        ),
        component: AuditTrailComponent,
        inputs: {
          searchString$: this.searchString$,
          object: this.dialogData.exportTemplate,
          hasOwner: true,
          timeCreatedKey: 'time_created',
          timeModifiedKey: 'time_modified',
        },
      });
    }
  }

  protected override performSave(
    formData: any,
  ): DialogCloseOptions<ExportTemplateDialogResult> {
    const isUpdate = this.isUpdateMode && !!this.dialogData.exportTemplate;

    const saved = this.saveExportTemplateMutation.mutateAsync({
      id: isUpdate ? this.dialogData.exportTemplate.id : null,
      template: {
        output_format_id: formData.output_format_id,
        name: formData.name,
        file_path: formData.file_path,
        html_template_file_path: formData.html_template_file_path,
        language_id: formData.language_id,
        is_default: formData.is_default,
        companies: formData.companies,
      },
    });

    // Keeps the promise "handled" so hosts that ignore the result do not produce
    // an unhandled rejection; failure state lives on the mutation.
    saved.catch(() => undefined);

    return {
      reason: isUpdate ? DialogCloseReason.Update : DialogCloseReason.Save,
      data: { exportTemplate: formData, saved },
    };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    // * all valid, proceed with save
    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);
    this.dirtyHandler.closeImmediately(closeOptions);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
